import { Image as ImageIcon, Lock, LogOut, RefreshCw, Save, Upload } from "lucide-react";
import type { ChangeEvent, FormEvent } from "react";
import { useEffect, useState } from "react";
import { menuItems as fallbackMenu, services as fallbackServices } from "../config/catalog";
import {
  adminLogin,
  adminLogout,
  adminOrders,
  adminSession,
  adminUploadMedia,
  adminUpsertMenu,
  adminUpsertService,
  getMenu,
  getServices
} from "../lib/api";
import { formatCurrency } from "../lib/format";
import type { MenuItem, Service } from "../types";
import { Button } from "../components/Button";
import { SectionHeader } from "../components/SectionHeader";

type AdminTab = "menu" | "services" | "orders";
type OrderRow = { id: string; customerEmail: string; status: string; totalCents: number; createdAt: string };

export function AdminPage() {
  const [authenticated, setAuthenticated] = useState(false);
  const [tab, setTab] = useState<AdminTab>("menu");
  const [menu, setMenu] = useState<MenuItem[]>(fallbackMenu);
  const [services, setServices] = useState<Service[]>(fallbackServices);
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [message, setMessage] = useState("");

  async function load() {
    setMenu(await getMenu());
    setServices(await getServices());
    if (authenticated) setOrders(await adminOrders());
  }

  useEffect(() => {
    void adminSession()
      .then((session) => setAuthenticated(session.authenticated))
      .catch(() => setAuthenticated(false));
  }, []);

  useEffect(() => {
    void load();
  }, [authenticated]);

  if (!authenticated) {
    return <AdminLogin onSuccess={() => setAuthenticated(true)} />;
  }

  return (
    <main className="admin-shell">
      <section className="admin-topbar">
        <div>
          <p className="eyebrow">Bistro manager</p>
          <h1>OJ's Bistro Manager</h1>
        </div>
        <div className="admin-actions">
          <Button variant="secondary" onClick={load}>
            <RefreshCw size={16} /> Refresh
          </Button>
          <Button
            variant="ghost"
            onClick={async () => {
              await adminLogout();
              setAuthenticated(false);
            }}
          >
            <LogOut size={16} /> Log out
          </Button>
        </div>
      </section>

      <nav className="admin-tabs">
        {(["menu", "services", "orders"] as const).map((item) => (
          <button key={item} type="button" className={tab === item ? "active" : ""} onClick={() => setTab(item)}>
            {item}
          </button>
        ))}
      </nav>

      {message ? <p className="admin-message">{message}</p> : null}
      {tab === "menu" ? <MenuEditor items={menu} onSave={async (item) => saveMenu(item, setMessage, load)} /> : null}
      {tab === "services" ? (
        <ServiceEditor items={services} onSave={async (service) => saveService(service, setMessage, load)} />
      ) : null}
      {tab === "orders" ? <OrdersTable orders={orders} /> : null}
    </main>
  );
}

function AdminLogin({ onSuccess }: { onSuccess: () => void }) {
  const [error, setError] = useState("");

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    try {
      const session = await adminLogin(String(form.get("password")));
      if (session.authenticated) onSuccess();
    } catch {
      setError("That password was not accepted.");
    }
  }

  return (
    <main className="admin-login">
      <form onSubmit={handleSubmit}>
        <Lock size={34} />
        <h1>Admin portal</h1>
        <p>Manage menu items, services, and orders.</p>
        <input type="password" name="password" placeholder="Password" required />
        {error ? <span>{error}</span> : null}
        <Button type="submit">Log in</Button>
      </form>
    </main>
  );
}

function MenuEditor({ items, onSave }: { items: MenuItem[]; onSave: (item: MenuItem) => Promise<void> }) {
  const [selected, setSelected] = useState<MenuItem>(items[0]);

  useEffect(() => {
    if (items[0]) setSelected(items[0]);
  }, [items]);

  if (!selected) return null;

  return (
    <section className="admin-grid">
      <EntityList items={items} activeId={selected.id} getLabel={(item) => item.name} onSelect={setSelected} />
      <MenuForm item={selected} onChange={setSelected} onSave={onSave} />
    </section>
  );
}

function ServiceEditor({ items, onSave }: { items: Service[]; onSave: (item: Service) => Promise<void> }) {
  const [selected, setSelected] = useState<Service>(items[0]);

  useEffect(() => {
    if (items[0]) setSelected(items[0]);
  }, [items]);

  if (!selected) return null;

  return (
    <section className="admin-grid">
      <EntityList items={items} activeId={selected.id} getLabel={(item) => item.name} onSelect={setSelected} />
      <ServiceForm item={selected} onChange={setSelected} onSave={onSave} />
    </section>
  );
}

function EntityList<T extends { id: string }>({
  items,
  activeId,
  getLabel,
  onSelect
}: {
  items: T[];
  activeId: string;
  getLabel: (item: T) => string;
  onSelect: (item: T) => void;
}) {
  return (
    <aside className="entity-list">
      {items.map((item) => (
        <button key={item.id} type="button" className={item.id === activeId ? "active" : ""} onClick={() => onSelect(item)}>
          {getLabel(item)}
        </button>
      ))}
    </aside>
  );
}

function MenuForm({
  item,
  onChange,
  onSave
}: {
  item: MenuItem;
  onChange: (item: MenuItem) => void;
  onSave: (item: MenuItem) => Promise<void>;
}) {
  return (
    <form className="admin-form" onSubmit={(event) => submitEntity(event, item, onSave)}>
      <SectionHeader title="Edit menu item" />
      <Field label="Name" value={item.name} onChange={(name) => onChange({ ...item, name })} />
      <Field label="Subtitle" value={item.subtitle} onChange={(subtitle) => onChange({ ...item, subtitle })} />
      <Field label="Category" value={item.category} onChange={(category) => onChange({ ...item, category })} />
      <ImageUploadField value={item.image} onChange={(image) => onChange({ ...item, image })} />
      <Field
        label="Price in cents"
        value={String(item.priceCents)}
        onChange={(price) => onChange({ ...item, priceCents: Number(price) })}
      />
      <label>
        Description
        <textarea value={item.description} onChange={(event) => onChange({ ...item, description: event.target.value })} />
      </label>
      <label className="toggle-row">
        <input type="checkbox" checked={item.featured} onChange={(event) => onChange({ ...item, featured: event.target.checked })} />
        Featured on homepage
      </label>
      <label className="toggle-row">
        <input type="checkbox" checked={item.active} onChange={(event) => onChange({ ...item, active: event.target.checked })} />
        Available on website
      </label>
      <Button type="submit">
        <Save size={16} /> Save item
      </Button>
    </form>
  );
}

function ServiceForm({
  item,
  onChange,
  onSave
}: {
  item: Service;
  onChange: (item: Service) => void;
  onSave: (item: Service) => Promise<void>;
}) {
  return (
    <form className="admin-form" onSubmit={(event) => submitEntity(event, item, onSave)}>
      <SectionHeader title="Edit service" />
      <Field label="Name" value={item.name} onChange={(name) => onChange({ ...item, name })} />
      <ImageUploadField value={item.image} onChange={(image) => onChange({ ...item, image })} />
      <Field
        label="Full price in cents"
        value={String(item.priceCents)}
        onChange={(price) => onChange({ ...item, priceCents: Number(price) })}
      />
      <Field
        label="Deposit in cents"
        value={String(item.depositCents)}
        onChange={(deposit) => onChange({ ...item, depositCents: Number(deposit) })}
      />
      <label>
        Description
        <textarea value={item.description} onChange={(event) => onChange({ ...item, description: event.target.value })} />
      </label>
      <label className="toggle-row">
        <input type="checkbox" checked={item.active} onChange={(event) => onChange({ ...item, active: event.target.checked })} />
        Available on website
      </label>
      <Button type="submit">
        <Save size={16} /> Save service
      </Button>
    </form>
  );
}

function Field({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label>
      {label}
      <input value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function ImageUploadField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  async function handleUpload(event: ChangeEvent<HTMLInputElement>) {
    const file = event.currentTarget.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const upload = await adminUploadMedia(file);
      onChange(upload.url);
    } catch {
      setError("Upload did not finish. Try a smaller image or sign in again.");
    } finally {
      setUploading(false);
      event.currentTarget.value = "";
    }
  }

  return (
    <div className="image-upload-field">
      <Field label="Image path" value={value} onChange={onChange} />
      <div className="image-upload-tools">
        <div className="image-upload-preview">
          {value ? <img src={value} alt="" /> : <ImageIcon size={28} />}
        </div>
        <label className="file-upload-button">
          <Upload size={16} />
          <span>{uploading ? "Uploading..." : "Upload image"}</span>
          <input type="file" accept="image/*" disabled={uploading} onChange={handleUpload} />
        </label>
      </div>
      {error ? <span className="field-error">{error}</span> : null}
    </div>
  );
}

function OrdersTable({ orders }: { orders: OrderRow[] }) {
  return (
    <section className="admin-panel">
      <SectionHeader title="Orders" />
      <div className="orders-table">
        {orders.length ? (
          orders.map((order) => (
            <article key={order.id}>
              <span>{order.id}</span>
              <span>{order.customerEmail || "No email"}</span>
              <span>{order.status}</span>
              <strong>{formatCurrency(order.totalCents)}</strong>
              <span>{new Date(order.createdAt).toLocaleDateString()}</span>
            </article>
          ))
        ) : (
          <p>No orders yet.</p>
        )}
      </div>
    </section>
  );
}

async function submitEntity<T>(event: FormEvent<HTMLFormElement>, item: T, save: (item: T) => Promise<void>) {
  event.preventDefault();
  await save(item);
}

async function saveMenu(item: MenuItem, setMessage: (message: string) => void, reload: () => Promise<void>) {
  try {
    await adminUpsertMenu(item);
    setMessage(`${item.name} saved.`);
    await reload();
  } catch {
    setMessage(`${item.name} could not be saved. Sign in again and retry.`);
  }
}

async function saveService(item: Service, setMessage: (message: string) => void, reload: () => Promise<void>) {
  try {
    await adminUpsertService(item);
    setMessage(`${item.name} saved.`);
    await reload();
  } catch {
    setMessage(`${item.name} could not be saved. Sign in again and retry.`);
  }
}
